import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

/**
 * Return integration provider values matching the Prisma schema.
 * Use this for RPC schemas and anywhere that needs the full list of providers.
 */
export const returnIntegrationProviderValues = [
  "aims360",
  "apparelmagic",
  "blueyonder",
  "brightpearl",
  "cloudx",
  "extensiv",
  "flexport",
  "inmar",
  "netsuite",
  "returnbear",
  "sfom",
  "shipbob",
  "shipedge",
  "shiphero",
  "ryder",
  "trackstar",
  "shipmonk",
  "deposco_old",
  "deposco",
  "bluecherry",
] as const;

export type ReturnIntegrationProviderValue =
  (typeof returnIntegrationProviderValues)[number];

export const ReturnIntegrationProviderSchema = z.enum(
  returnIntegrationProviderValues,
);

export enum IntegrationProviders {
  AIMS360 = "aims360",
  APPAREL_MAGIC = "apparelmagic",
  BLUE_CHERRY = "bluecherry",
  BRIGHTPEARL = "brightpearl",
  CLOUDX = "cloudx",
  EXTENSIV = "extensiv",
  FLEXPORT = "flexport",
  NETSUITE = "netsuite",
  SFOM = "sfom",
  RYDER = "ryder",
  SHIPBOB = "shipbob",
  SHIPEDGE = "shipedge",
  SHIPHERO = "shiphero",
  SHIPMONK = "shipmonk",
  TRACKSTAR = "trackstar",
  DEPOSCO = "deposco",
}

export enum TrackstarReturnProvider {
  SHIPSTREAM = "shipstream",
  BLUEBOX = "bluebox",
  SHIPMONK = "shipmonk",
  DEPOSCO = "deposco",
  NIMBLE = "nimble",
  WHIPLASH_RYDER = "whiplash",
  CIRRO = "cirro",
}

export const IntegrationProviderMerchantNames: Record<
  IntegrationProviders,
  string
> = {
  [IntegrationProviders.AIMS360]: "Aims360",
  [IntegrationProviders.APPAREL_MAGIC]: "Apparel Magic",
  [IntegrationProviders.BLUE_CHERRY]: "BlueCherry",
  [IntegrationProviders.BRIGHTPEARL]: "Brightpearl",
  [IntegrationProviders.CLOUDX]: "Bergen",
  [IntegrationProviders.EXTENSIV]: "Extensiv",
  [IntegrationProviders.FLEXPORT]: "Flexport",
  [IntegrationProviders.NETSUITE]: "NetSuite",
  [IntegrationProviders.SFOM]: "SFOM",
  [IntegrationProviders.RYDER]: "Ryder",
  [IntegrationProviders.SHIPBOB]: "ShipBob",
  [IntegrationProviders.SHIPEDGE]: "Shipedge",
  [IntegrationProviders.SHIPHERO]: "ShipHero",
  [IntegrationProviders.SHIPMONK]: "ShipMonk",
  [IntegrationProviders.TRACKSTAR]: "Trackstar",
  [IntegrationProviders.DEPOSCO]: "Deposco",
};

export const TrackstarReturnProviderMerchantNames: Record<
  TrackstarReturnProvider,
  string
> = {
  [TrackstarReturnProvider.SHIPSTREAM]: "ShipStream",
  [TrackstarReturnProvider.BLUEBOX]: "BlueBox",
  [TrackstarReturnProvider.SHIPMONK]: "ShipMonk",
  [TrackstarReturnProvider.DEPOSCO]: "Deposco",
  [TrackstarReturnProvider.NIMBLE]: "Nimble",
  [TrackstarReturnProvider.WHIPLASH_RYDER]: "Whiplash Ryder",
  [TrackstarReturnProvider.CIRRO]: "Cirro",
};

function isIntegrationProvider(
  name: IntegrationProviders | TrackstarReturnProvider,
): name is IntegrationProviders {
  return Object.values(IntegrationProviders).includes(
    name as IntegrationProviders,
  );
}

function isTrackstarReturnProvider(
  name: IntegrationProviders | TrackstarReturnProvider,
): name is TrackstarReturnProvider {
  return Object.values(TrackstarReturnProvider).includes(
    name as TrackstarReturnProvider,
  );
}

export const getMerchantFacingIntegrationName = (
  integrationName: IntegrationProviders | TrackstarReturnProvider,
): string => {
  if (isIntegrationProvider(integrationName)) {
    return IntegrationProviderMerchantNames[integrationName];
  }
  if (isTrackstarReturnProvider(integrationName)) {
    return TrackstarReturnProviderMerchantNames[integrationName];
  }

  // Fallback to original name if not found
  return integrationName;
};

export const getMerchantMessage = (errorCode: ReturnIntegrationErrorCode) => {
  switch (errorCode) {
    case BlueCherryIntegrationErrorCode.BLUE_CHERRY_GENERAL_ERROR:
      return "The BlueCherry integration failed to create the RMA. Please retry using the actions menu below, or create the RMA manually in your BlueCherry system.";
    case BlueCherryIntegrationErrorCode.BLUE_CHERRY_NO_INTEGRATION:
      return "The BlueCherry integration is not configured. Please set up the BlueCherry integration in your settings and retry using the actions menu below.";
    case Aims360IntegrationErrorCode.AIMS360_GENERAL_ERROR:
      return "The Aims360 integration failed to create the RMA. Please retry using the actions menu below, or create the RMA manually in your Aims360 system.";
    case Aims360IntegrationErrorCode.AIMS360_NO_INVOICES_FOUND:
      return "No Aims360 invoices were found for this order. Please verify that the order has been invoiced in Aims360, or create the RMA manually in your Aims360 system.";
    case Aims360IntegrationErrorCode.AIMS360_NO_AVAILABLE_QUANTITY:
      return "No available quantity was found for one or more items in this return. The items may have already been returned in Aims360. Please verify the available quantity in Aims360, or create the RMA manually in your Aims360 system.";
    case FlexportIntegrationErrorCode.FLEXPORT_TRACKING_CODE_NOT_FOUND:
      return "Flexport could not find the tracking code for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_CARRIER_NOT_FOUND:
      return "Flexport could not find the carrier for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_INVALID_LOCATION:
      return "The return location is invalid or missing in your shipping and integration settings. Please add the location and retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_DUPLICATE_ENTRY:
      return "This return already exists in Flexport with the same tracking code. Check your Flexport system to confirm the return was created successfully, then dismiss this error.";
    case FlexportIntegrationErrorCode.FLEXPORT_PRODUCT_NOT_FOUND:
      return "One or more products in this return were not found in Flexport. Please retry using the actions menu below, excluding any non-physical items, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_BAD_REQUEST:
      return "Flexport rejected the request to create the RMA, often due to non-physical products. Please retry using the actions menu below, excluding non-physical items, or create the RMA manually in your Flexport system.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_NOT_FOUND:
      return "The sales order was not found in NetSuite. Please verify the order exists and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED:
      return "NetSuite could not initialize the return authorization, likely because the sales order is not yet fulfilled. Please fulfill the order and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS:
      return "No valid line items were found for the NetSuite return authorization. Please verify SKUs and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR:
      return "NetSuite encountered a group line error when creating the RMA. See this link for details: https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_163306924686.html Retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR:
      return "This return authorization already exists in NetSuite. Check your NetSuite system to confirm it was created successfully, then dismiss this error.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_FIELD_VALUE_ERROR:
      return "NetSuite rejected invalid field values, likely due to inactive or matrix items. Please correct them and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR:
      return "Access to NetSuite has been lost. Please re-authenticate and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR:
      return "NetSuite encountered an unexpected SuiteScript error. Please contact support and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR:
      return "NetSuite requires the sales order to be fulfilled before creating a return authorization. Please fulfill the order and retry using the actions menu below.";
    case NetsuiteIntegrationErrorCode.NETSUITE_PERMISSION_VIOLATION_LISTS_TAX:
      return "NetSuite requires permission (lists -> tax details tab) to create a return authorization. Please check your NetSuite permissions and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR:
      return "NetSuite inventory detail quantity error. The Sales Order has multiple line items with the same SKU, and the system cannot determine which line to apply the return to. Manual RMA creation is required.";
    case SfomIntegrationErrorCode.SFOM_DIDNT_RECEIVE_RMA_ID:
      return "SFOM did not return an RMA ID. Please retry using the actions menu below, or create the RMA manually in your SFOM system.";
    case RyderIntegrationErrorCode.RYDER_NO_INTEGRATION:
      return "The Ryder integration is not configured. Please set up the Ryder integration in your settings and retry using the actions menu below.";
    case RyderIntegrationErrorCode.RYDER_MISSING_REFRESH_TOKEN:
      return "Ryder access has been lost. Please re-authorize the Ryder integration in your settings and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_ORDERS_FOUND:
      return "No Ryder order was found for this Shopify order. Please verify the order exists in Ryder and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_WAREHOUSES:
      return "No Ryder warehouses were found. Please verify your Ryder account has warehouses configured and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_WAREHOUSE_NOT_MATCHED:
      return "No Ryder warehouse could be matched to the return address. Please verify that your Ryder warehouses are configured for returns and that the return address matches a warehouse, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_RETURN_ID:
      return "Ryder did not return an RMA ID after creation. Please check your Ryder system to verify if the return was created, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_DUPLICATE_REFERENCE:
      return "This return already exists in Ryder. Please check your Ryder system to confirm it was created successfully, then dismiss this error.";
    case RyderIntegrationErrorCode.RYDER_NO_RETURN_ITEMS:
      return "No returnable items were found for this return in Ryder. Please verify the products have valid SKUs and retry using the actions menu below, or create the RMA manually in Ryder.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR:
      return "No ShipBob order was found for this Shopify order. Please update the reference ID and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_FULFILLMENT_CENTERS:
      return "No ShipBob fulfillment centers were found. Please verify your ShipBob account has fulfillment centers configured and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_REDO_CHANNEL:
      return "No Redo channel was found in ShipBob. Please ensure the Redo channel is set up in your ShipBob account and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_REFERENCE_IDS:
      return "No valid product reference IDs were found for this return. Please verify the products have valid SKUs or variant IDs and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_DUPLICATE_REFERENCE_ID:
      return "This return already exists in ShipBob. Please check your ShipBob system to confirm it was created successfully, then dismiss this error.";
    case ShipbobIntegrationErrorCode.SHIPBOB_MISSING_REFRESH_TOKEN:
      return "ShipBob access has been lost. Please re-authorize the ShipBob integration in your settings and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_INTEGRATION:
      return "The ShipBob integration is not configured. Please set up the ShipBob integration in your settings and retry using the actions menu below.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_RETURN_ID:
      return "ShipBob did not return an RMA ID after creation. Please check your ShipBob system to verify if the return was created, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID:
      return "A duplicate reference ID error occurred but the existing return could not be found in ShipBob. Please check your ShipBob system or create the RMA manually.";
    case ShipbobIntegrationErrorCode.SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED:
      return "No ShipBob fulfillment center could be matched to the return address. Please verify that your ShipBob fulfillment centers are configured for returns and that the return address matches a fulfillment center, or create the RMA manually in ShipBob.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_ORDER_NOT_FOUND:
      return "No Apparel Magic order was found for this return. Please verify the order and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_RA_NOT_DEFINED:
      return "The Apparel Magic RA number is not defined for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_MULTIPLE_ORDERS:
      return "This return contains multiple orders, which is not supported by Apparel Magic. Please create separate RMAs manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_NO_ITEMS_TO_RETURN:
      return "No returnable items were found in Apparel Magic for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_INSUFFICIENT_QUANTITY:
      return "The quantity of items in Apparel Magic is insufficient for this return. Please verify the quantity and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_UNAUTHORIZED_ERROR:
      return "Access to Apparel Magic has been lost or the API token is invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND:
      return "No Brightpearl sales order row was found for the returned products. Please verify the products and retry using the actions menu below, or create the RMA manually in Brightpearl.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND:
      return "The original sales order was not found in Brightpearl. Please ensure it exists and retry using the actions menu below, or create it manually.";
    case CloudxIntegrationErrorCode.CLOUDX_ITEM_MASTER_ERROR:
      return "Bergen encountered an item master error. Please verify SKUs and retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_STATE_ERROR:
      return "Bergen requires a valid US state code for returns. Please retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_UPC_SKU_NOT_FOUND:
      return "One or more products in this return are missing a UPC or SKU. Please verify that all products have a valid UPC/SKU configured in Shopify, or create the receiving ticket manually in Bergen.";
    case ExtensivIntegrationErrorCode.EXTENSIV_AUTHENTICATION_ERROR:
      return "Access to Extensiv has been lost or the API credentials are invalid. Please re-authenticate and retry using the actions menu below, or create the receiver manually in Extensiv.";
    case ExtensivIntegrationErrorCode.EXTENSIV_BAD_REQUEST:
      return "Extensiv rejected the request to create the receiver. Please verify all product SKUs and configuration, then retry using the actions menu below, or create the receiver manually in Extensiv.";
    case ExtensivIntegrationErrorCode.EXTENSIV_DUPLICATE_ENTRY:
      return "This return already exists in Extensiv. Please check your Extensiv system to confirm it was created successfully, then dismiss this error.";
    case ShipheroIntegrationErrorCode.SHIPHERO_ORDER_NOT_FOUND:
      return "No ShipHero order was found for this Shopify order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_SKU_MISSING_ON_ORDER:
      return "One or more SKUs were not found on the original ShipHero order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_ACCOUNT_CANCELED:
      return "The ShipHero account has been canceled or is inactive. Please verify your ShipHero account status and re-authenticate if needed, or create the RMA manually in ShipHero.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND:
      return "The order was not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ITEM_NOT_FOUND:
      return "One or more items from the return were not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND:
      return "One or more inventory items were not found in Whiplash Ryder WMS. Make sure that inventory for the SKUs in this return are properly imported into Whiplash Ryder WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.WHIPLASH_RYDER_SKU_NOT_FOUND:
      return "One or more SKUs in this return were not found on the Whiplash Ryder WMS order. Please ensure the SKUs exist on the corresponding WMS order and are properly configured, then retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.RETURN_ALREADY_EXISTS:
      return "This return already exists in the WMS. Please check your system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.NO_WAREHOUSE_FOUND_FOR_SHIPMENT:
      return "No warehouse was found for this return shipment. Please add the warehouse and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.NO_ORDER_ID_FOUND_FOR_RETURN:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER:
      return "The order was not found in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.SHIPSTREAM_DELIVERY_ALREADY_EXISTS:
      return "The ShipStream delivery already exists. Please check your system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.SHIPSTREAM_SKU_NOT_FOUND:
      return "One or more SKUs in this return were not found in ShipStream. Please ensure the SKUs exist on the associated WMS order and retry using the actions menu below, or create the RMA manually in ShipStream.";
    case TrackstarIntegrationErrorCode.RYDER_ORDER_NOT_YET_SHIPPED:
      return "Unable to create a return because the order has not yet shipped in RyderShip. The order must be marked as shipped in RyderShip before a return can be created. Please go and mark the order as shipped and retry using the actions menu below.";
    case TrackstarIntegrationErrorCode.DEPOSCO_DUPLICATE_ENTRY:
      return "This return already exists in Deposco. Please check your Deposco system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.DEPOSCO_ORDER_NOT_FOUND:
      return "The order was not found in Deposco. Please verify the order exists in your Deposco system and retry using the actions menu below, or create the RMA manually in Deposco.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_NO_INTEGRATION:
      return "The ShipMonk integration is not configured. Please set up the ShipMonk integration in your settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_DUPLICATE_ENTRY:
      return "This return already exists in ShipMonk. Please check your ShipMonk system to confirm it was created successfully, then dismiss this error.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_BAD_REQUEST:
      return "ShipMonk rejected the request to create the RMA. Please verify all product SKUs and configuration, then retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_SKU_NOT_FOUND:
      return "One or more product SKUs in this return were not found in ShipMonk. Please verify that all products have valid SKUs configured, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_WAREHOUSE_NOT_CONFIGURED:
      return "No ShipMonk warehouse is configured for this integration. Please configure the warehouse in your ShipMonk integration settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_STORE_NOT_CONFIGURED:
      return "No ShipMonk store is configured for this integration. Please configure the store in your ShipMonk integration settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_AUTHENTICATION_ERROR:
      return "Access to ShipMonk has been lost or the API key is invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_NO_WAREHOUSES:
      return "No ShipMonk warehouses were found. Please verify your ShipMonk account has warehouses configured and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_WAREHOUSE_NOT_MATCHED:
      return "No ShipMonk warehouse could be matched to the return address. Please verify that your ShipMonk warehouses are configured for returns and that the return address matches a warehouse, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_ORDER_NOT_FOUND:
      return "No ShipMonk order was found for this return. Please verify the order exists in ShipMonk and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case DeposcoIntegrationErrorCode.NO_INTEGRATION:
      return "The Deposco integration is not configured. Please set up the Deposco integration in your settings and retry using the actions menu below.";
    case DeposcoIntegrationErrorCode.NO_ORDER:
      return "No Deposco order was found for this return. Please verify the order exists in Deposco and retry using the actions menu below, or create the RMA manually in Deposco.";
    case DeposcoIntegrationErrorCode.NO_FACILITY_MATCH:
      return "No Deposco facility could be matched to the return address. Please verify that your Deposco facilities are configured for returns and that the return address matches a facility, or create the RMA manually in Deposco.";
    case DeposcoIntegrationErrorCode.API_ERROR:
      return "The Deposco API returned an error. Please retry using the actions menu below, or create the RMA manually in Deposco.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_DUPLICATE_ENTRY:
      return "This return already exists in Shipedge. Please check your Shipedge system to confirm it was created successfully, then dismiss this error.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_BAD_REQUEST:
      return "Shipedge rejected the request to create the RMA. Please verify all product SKUs exist in Shipedge and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_AUTHENTICATION_ERROR:
      return "Access to Shipedge has been lost or the API credentials are invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_ORDER_NOT_FOUND:
      return "The original order was not found in Shipedge. Please verify the order exists in Shipedge and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case UnknownErrorCode.UNKNOWN_ERROR:
      return "An unexpected error occurred during RMA creation. Please retry using the actions menu below, or create the RMA manually in your WMS. Contact support if this persists.";
    case GeneralErrorCode.LABEL_REQUIRED:
      return "A return shipping label is required before the RMA can be created. Please generate a return label and retry using the actions menu below, or create the RMA manually in your system.";
    default:
      assertNever(errorCode);
  }
};

// Individual integration error code enums
export enum BlueCherryIntegrationErrorCode {
  BLUE_CHERRY_GENERAL_ERROR = "BLUE_CHERRY_GENERAL_ERROR",
  BLUE_CHERRY_NO_INTEGRATION = "BLUE_CHERRY_NO_INTEGRATION",
}

export enum Aims360IntegrationErrorCode {
  AIMS360_GENERAL_ERROR = "AIMS360_GENERAL_ERROR",
  AIMS360_NO_INVOICES_FOUND = "AIMS360_NO_INVOICES_FOUND",
  AIMS360_NO_AVAILABLE_QUANTITY = "AIMS360_NO_AVAILABLE_QUANTITY",
}

export enum ApparelMagicIntegrationErrorCode {
  APPAREL_MAGIC_ORDER_NOT_FOUND = "APPAREL_MAGIC_ORDER_NOT_FOUND",
  APPAREL_MAGIC_RA_NOT_DEFINED = "APPAREL_MAGIC_RA_NOT_DEFINED",
  APPAREL_MAGIC_MULTIPLE_ORDERS = "APPAREL_MAGIC_MULTIPLE_ORDERS",
  APPAREL_MAGIC_NO_ITEMS_TO_RETURN = "APPAREL_MAGIC_NO_ITEMS_TO_RETURN",
  APPAREL_MAGIC_INSUFFICIENT_QUANTITY = "APPAREL_MAGIC_INSUFFICIENT_QUANTITY",
  APPAREL_MAGIC_UNAUTHORIZED_ERROR = "APPAREL_MAGIC_UNAUTHORIZED_ERROR",
}

export enum BrightpearlIntegrationErrorCode {
  BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND = "BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND",
  BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND = "BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND",
}

export enum CloudxIntegrationErrorCode {
  CLOUDX_ITEM_MASTER_ERROR = "CLOUDX_ITEM_MASTER_ERROR",
  CLOUDX_STATE_ERROR = "CLOUDX_STATE_ERROR",
  CLOUDX_UPC_SKU_NOT_FOUND = "CLOUDX_UPC_SKU_NOT_FOUND",
}

export enum ExtensivIntegrationErrorCode {
  EXTENSIV_AUTHENTICATION_ERROR = "EXTENSIV_AUTHENTICATION_ERROR",
  EXTENSIV_BAD_REQUEST = "EXTENSIV_BAD_REQUEST",
  EXTENSIV_DUPLICATE_ENTRY = "EXTENSIV_DUPLICATE_ENTRY",
}

export enum FlexportIntegrationErrorCode {
  FLEXPORT_TRACKING_CODE_NOT_FOUND = "FLEXPORT_TRACKING_CODE_NOT_FOUND",
  FLEXPORT_CARRIER_NOT_FOUND = "FLEXPORT_CARRIER_NOT_FOUND",
  FLEXPORT_INVALID_LOCATION = "FLEXPORT_INVALID_LOCATION",
  FLEXPORT_DUPLICATE_ENTRY = "FLEXPORT_DUPLICATE_ENTRY",
  FLEXPORT_PRODUCT_NOT_FOUND = "FLEXPORT_PRODUCT_NOT_FOUND",
  FLEXPORT_BAD_REQUEST = "FLEXPORT_BAD_REQUEST",
}

export enum NetsuiteIntegrationErrorCode {
  NETSUITE_SALES_ORDER_NOT_FOUND = "NETSUITE_SALES_ORDER_NOT_FOUND",
  NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED = "NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED",
  NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS = "NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS",
  NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR = "NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR",
  NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR = "NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR",
  NETSUITE_INVALID_FIELD_VALUE_ERROR = "NETSUITE_INVALID_FIELD_VALUE_ERROR",
  NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR = "NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR",
  NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR = "NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR",
  NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR = "NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR",
  NETSUITE_PERMISSION_VIOLATION_LISTS_TAX = "NETSUITE_PERMISSION_VIOLATION_LISTS_TAX",
  NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR = "NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR",
}

export enum SfomIntegrationErrorCode {
  SFOM_DIDNT_RECEIVE_RMA_ID = "SFOM_DIDNT_RECEIVE_RMA_ID",
}

export enum ShipedgeIntegrationErrorCode {
  SHIPEDGE_DUPLICATE_ENTRY = "SHIPEDGE_DUPLICATE_ENTRY",
  SHIPEDGE_BAD_REQUEST = "SHIPEDGE_BAD_REQUEST",
  SHIPEDGE_AUTHENTICATION_ERROR = "SHIPEDGE_AUTHENTICATION_ERROR",
  SHIPEDGE_ORDER_NOT_FOUND = "SHIPEDGE_ORDER_NOT_FOUND",
}

export enum RyderIntegrationErrorCode {
  RYDER_NO_INTEGRATION = "RYDER_NO_INTEGRATION",
  RYDER_MISSING_REFRESH_TOKEN = "RYDER_MISSING_REFRESH_TOKEN",
  RYDER_NO_ORDERS_FOUND = "RYDER_NO_ORDERS_FOUND",
  RYDER_NO_WAREHOUSES = "RYDER_NO_WAREHOUSES",
  RYDER_WAREHOUSE_NOT_MATCHED = "RYDER_WAREHOUSE_NOT_MATCHED",
  RYDER_NO_RETURN_ID = "RYDER_NO_RETURN_ID",
  RYDER_DUPLICATE_REFERENCE = "RYDER_DUPLICATE_REFERENCE",
  RYDER_NO_RETURN_ITEMS = "RYDER_NO_RETURN_ITEMS",
}

export enum ShipbobIntegrationErrorCode {
  SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR = "SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR",
  SHIPBOB_NO_FULFILLMENT_CENTERS = "SHIPBOB_NO_FULFILLMENT_CENTERS",
  SHIPBOB_NO_REDO_CHANNEL = "SHIPBOB_NO_REDO_CHANNEL",
  SHIPBOB_NO_REFERENCE_IDS = "SHIPBOB_NO_REFERENCE_IDS",
  SHIPBOB_DUPLICATE_REFERENCE_ID = "SHIPBOB_DUPLICATE_REFERENCE_ID",
  SHIPBOB_MISSING_REFRESH_TOKEN = "SHIPBOB_MISSING_REFRESH_TOKEN",
  SHIPBOB_NO_INTEGRATION = "SHIPBOB_NO_INTEGRATION",
  SHIPBOB_NO_RETURN_ID = "SHIPBOB_NO_RETURN_ID",
  SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID = "SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID",
  SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED = "SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED",
}

export enum DeposcoIntegrationErrorCode {
  NO_INTEGRATION = "DEPOSCO_NO_INTEGRATION",
  NO_ORDER = "DEPOSCO_NO_ORDER",
  NO_FACILITY_MATCH = "DEPOSCO_NO_FACILITY_MATCH",
  API_ERROR = "DEPOSCO_API_ERROR",
}

export enum ShipmonkIntegrationErrorCode {
  SHIPMONK_NO_INTEGRATION = "SHIPMONK_NO_INTEGRATION",
  SHIPMONK_DUPLICATE_ENTRY = "SHIPMONK_DUPLICATE_ENTRY",
  SHIPMONK_BAD_REQUEST = "SHIPMONK_BAD_REQUEST",
  SHIPMONK_SKU_NOT_FOUND = "SHIPMONK_SKU_NOT_FOUND",
  SHIPMONK_WAREHOUSE_NOT_CONFIGURED = "SHIPMONK_WAREHOUSE_NOT_CONFIGURED",
  SHIPMONK_STORE_NOT_CONFIGURED = "SHIPMONK_STORE_NOT_CONFIGURED",
  SHIPMONK_AUTHENTICATION_ERROR = "SHIPMONK_AUTHENTICATION_ERROR",
  SHIPMONK_NO_WAREHOUSES = "SHIPMONK_NO_WAREHOUSES",
  SHIPMONK_WAREHOUSE_NOT_MATCHED = "SHIPMONK_WAREHOUSE_NOT_MATCHED",
  SHIPMONK_ORDER_NOT_FOUND = "SHIPMONK_ORDER_NOT_FOUND",
}

export enum ShipheroIntegrationErrorCode {
  SHIPHERO_ORDER_NOT_FOUND = "SHIPHERO_ORDER_NOT_FOUND",
  SHIPHERO_SKU_MISSING_ON_ORDER = "SHIPHERO_SKU_MISSING_ON_ORDER",
  SHIPHERO_ACCOUNT_CANCELED = "SHIPHERO_ACCOUNT_CANCELED",
}

export enum TrackstarIntegrationErrorCode {
  SHIPSTREAM_DELIVERY_ALREADY_EXISTS = "SHIPSTREAM_DELIVERY_ALREADY_EXISTS",
  SHIPSTREAM_SKU_NOT_FOUND = "SHIPSTREAM_SKU_NOT_FOUND",
  ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API = "ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API",
  ORDER_NOT_FOUND = "ORDER_NOT_FOUND",
  ORDER_ITEM_NOT_FOUND = "ORDER_ITEM_NOT_FOUND",
  WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND = "WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND",
  WHIPLASH_RYDER_SKU_NOT_FOUND = "WHIPLASH_RYDER_SKU_NOT_FOUND",
  RETURN_ALREADY_EXISTS = "RETURN_ALREADY_EXISTS",
  NO_WAREHOUSE_FOUND_FOR_SHIPMENT = "NO_WAREHOUSE_FOUND_FOR_SHIPMENT",
  NO_ORDER_ID_FOUND_FOR_RETURN = "NO_ORDER_ID_FOUND_FOR_RETURN",
  ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER = "ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER",
  RYDER_ORDER_NOT_YET_SHIPPED = "RYDER_ORDER_NOT_YET_SHIPPED",
  DEPOSCO_DUPLICATE_ENTRY = "DEPOSCO_DUPLICATE_ENTRY",
  DEPOSCO_ORDER_NOT_FOUND = "DEPOSCO_ORDER_NOT_FOUND",
}

export enum GeneralErrorCode {
  LABEL_REQUIRED = "LABEL_REQUIRED",
}

export enum UnknownErrorCode {
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export enum ReturnIntegrationSyncResultType {
  NoSetUp = "NO_SET_UP",
  Failure = "FAILURE",
  AlreadyExists = "ALREADY_EXISTS",
  LogicallyNotNeeded = "LOGICALLY_NOT_NEEDED",
  LabelRequired = "LABEL_REQUIRED",
  Success = "SUCCESS",
}

// Zod schemas for individual integration error codes
export const BlueCherryIntegrationErrorCodeZodSchema = createEnum(
  BlueCherryIntegrationErrorCode,
  { enumName: "BlueCherryIntegrationErrorCode" },
);
export const Aims360IntegrationErrorCodeZodSchema = createEnum(
  Aims360IntegrationErrorCode,
  { enumName: "Aims360IntegrationErrorCode" },
);
export const ApparelMagicIntegrationErrorCodeZodSchema = createEnum(
  ApparelMagicIntegrationErrorCode,
  { enumName: "ApparelMagicIntegrationErrorCode" },
);
export const BrightpearlIntegrationErrorCodeZodSchema = createEnum(
  BrightpearlIntegrationErrorCode,
  { enumName: "BrightpearlIntegrationErrorCode" },
);
export const CloudxIntegrationErrorCodeZodSchema = createEnum(
  CloudxIntegrationErrorCode,
  { enumName: "CloudxIntegrationErrorCode" },
);
export const ExtensivIntegrationErrorCodeZodSchema = createEnum(
  ExtensivIntegrationErrorCode,
  { enumName: "ExtensivIntegrationErrorCode" },
);
export const FlexportIntegrationErrorCodeZodSchema = createEnum(
  FlexportIntegrationErrorCode,
  { enumName: "FlexportIntegrationErrorCode" },
);
export const NetsuiteIntegrationErrorCodeZodSchema = createEnum(
  NetsuiteIntegrationErrorCode,
  { enumName: "NetsuiteIntegrationErrorCode" },
);
export const SfomIntegrationErrorCodeZodSchema = createEnum(
  SfomIntegrationErrorCode,
  { enumName: "SfomIntegrationErrorCode" },
);
export const ShipedgeIntegrationErrorCodeZodSchema = createEnum(
  ShipedgeIntegrationErrorCode,
  { enumName: "ShipedgeIntegrationErrorCode" },
);
export const RyderIntegrationErrorCodeZodSchema = createEnum(
  RyderIntegrationErrorCode,
  { enumName: "RyderIntegrationErrorCode" },
);
export const ShipbobIntegrationErrorCodeZodSchema = createEnum(
  ShipbobIntegrationErrorCode,
  { enumName: "ShipbobIntegrationErrorCode" },
);
export const ShipmonkIntegrationErrorCodeZodSchema = createEnum(
  ShipmonkIntegrationErrorCode,
  { enumName: "ShipmonkIntegrationErrorCode" },
);
export const DeposcoIntegrationErrorCodeZodSchema = createEnum(
  DeposcoIntegrationErrorCode,
  { enumName: "DeposcoIntegrationErrorCode" },
);
export const ShipheroIntegrationErrorCodeZodSchema = createEnum(
  ShipheroIntegrationErrorCode,
  { enumName: "ShipheroIntegrationErrorCode" },
);
export const TrackstarIntegrationErrorCodeZodSchema = createEnum(
  TrackstarIntegrationErrorCode,
  { enumName: "TrackstarIntegrationErrorCode" },
);
export const GeneralErrorCodeZodSchema = createEnum(GeneralErrorCode, {
  enumName: "GeneralErrorCode",
});
export const UnknownErrorCodeZodSchema = createEnum(UnknownErrorCode, {
  enumName: "UnknownErrorCode",
});

// Union type for all integration error codes
export type ReturnIntegrationErrorCode =
  | BlueCherryIntegrationErrorCode
  | ShipheroIntegrationErrorCode
  | BrightpearlIntegrationErrorCode
  | DeposcoIntegrationErrorCode
  | FlexportIntegrationErrorCode
  | NetsuiteIntegrationErrorCode
  | RyderIntegrationErrorCode
  | ShipbobIntegrationErrorCode
  | ShipmonkIntegrationErrorCode
  | ShipedgeIntegrationErrorCode
  | SfomIntegrationErrorCode
  | ApparelMagicIntegrationErrorCode
  | Aims360IntegrationErrorCode
  | CloudxIntegrationErrorCode
  | ExtensivIntegrationErrorCode
  | TrackstarIntegrationErrorCode
  | GeneralErrorCode
  | UnknownErrorCode;

// Zod schema for the union type
export const ReturnIntegrationErrorCodeZodSchema = z.union([
  BlueCherryIntegrationErrorCodeZodSchema,
  ShipheroIntegrationErrorCodeZodSchema,
  DeposcoIntegrationErrorCodeZodSchema,
  BrightpearlIntegrationErrorCodeZodSchema,
  FlexportIntegrationErrorCodeZodSchema,
  NetsuiteIntegrationErrorCodeZodSchema,
  RyderIntegrationErrorCodeZodSchema,
  ShipbobIntegrationErrorCodeZodSchema,
  ShipmonkIntegrationErrorCodeZodSchema,
  ShipedgeIntegrationErrorCodeZodSchema,
  SfomIntegrationErrorCodeZodSchema,
  ApparelMagicIntegrationErrorCodeZodSchema,
  Aims360IntegrationErrorCodeZodSchema,
  CloudxIntegrationErrorCodeZodSchema,
  ExtensivIntegrationErrorCodeZodSchema,
  TrackstarIntegrationErrorCodeZodSchema,
  GeneralErrorCodeZodSchema,
  UnknownErrorCodeZodSchema,
]);

export const ReturnIntegrationErrorCodeValues = [
  ...Object.values(BlueCherryIntegrationErrorCode),
  ...Object.values(ShipheroIntegrationErrorCode),
  ...Object.values(DeposcoIntegrationErrorCode),
  ...Object.values(BrightpearlIntegrationErrorCode),
  ...Object.values(FlexportIntegrationErrorCode),
  ...Object.values(NetsuiteIntegrationErrorCode),
  ...Object.values(RyderIntegrationErrorCode),
  ...Object.values(ShipbobIntegrationErrorCode),
  ...Object.values(ShipmonkIntegrationErrorCode),
  ...Object.values(ShipedgeIntegrationErrorCode),
  ...Object.values(SfomIntegrationErrorCode),
  ...Object.values(ApparelMagicIntegrationErrorCode),
  ...Object.values(Aims360IntegrationErrorCode),
  ...Object.values(CloudxIntegrationErrorCode),
  ...Object.values(ExtensivIntegrationErrorCode),
  ...Object.values(TrackstarIntegrationErrorCode),
  ...Object.values(GeneralErrorCode),
  ...Object.values(UnknownErrorCode),
];
export type ReturnIntegrationErrorCodeValues =
  (typeof ReturnIntegrationErrorCodeValues)[number];

export const ReturnIntegrationSyncResultTypeZodSchema = createEnum(
  ReturnIntegrationSyncResultType,
  { enumName: "ReturnIntegrationSyncResultType" },
);

// Following the pattern from the codebase where we have both the underscore version and the typed version
const ReturnIntegrationErrorCodeZodSchema_ =
  ReturnIntegrationErrorCodeZodSchema;
export const ReturnIntegrationErrorCodeZodSchemaFinal: z.ZodType<ReturnIntegrationErrorCode> =
  ReturnIntegrationErrorCodeZodSchema_;
export type ReturnIntegrationErrorCodeZod = z.infer<
  typeof ReturnIntegrationErrorCodeZodSchema_
>;

export enum NetSuiteTransactionType {
  CASH_SALE = "cash_sale",
  SALES_ORDER = "sales_order",
  BOTH = "both",
}

export const NetSuiteTransactionTypeZodSchema = createEnum(
  NetSuiteTransactionType,
  { enumName: "NetSuiteTransactionType" },
);

export enum NetSuiteAuthMethod {
  TBA = "tba",
  OAUTH2 = "oauth2",
}

export const NetSuiteAuthMethodZodSchema = createEnum(NetSuiteAuthMethod, {
  enumName: "NetSuiteAuthMethod",
});

export enum NetSuiteItemValueType {
  CREDIT = "credit",
  REFUND = "refund",
  TOTAL = "total",
  TOTAL_PRE_DISCOUNT = "total-pre-discount",
}

export const NetSuiteItemValueTypeZodSchema = createEnum(
  NetSuiteItemValueType,
  { enumName: "NetSuiteItemValueType" },
);
